/**
 * Seat pricing, ported from da-connect.Domain/Pricing/SeatPricing.cs.
 *
 * That file says it outright: "Stage 2 wires the same numbers into the client; both sides must
 * call arithmetic derived from this type, because a second implementation is how a customer gets
 * quoted one price on the plans page and charged another by Stripe." So this is a deliberate,
 * line-for-line port of the same rules — NOT a fresh price table. It takes the tiers off the live
 * Stripe price (GET /api/stripe/plans expands them), so a price edited in the Stripe Dashboard
 * changes what we quote without a code change.
 *
 * QUANTITY IS SEATS (users), never a council count. Councils are free and unlimited; a council
 * count reaching a Stripe quantity is the bug that was ripped out in 0d062917.
 *
 * Amounts are Stripe minor units (AUD cents) throughout.
 */

/** One entry of a Stripe price's `tiers`. `upTo: null` is Stripe's unbounded (`"inf"`) last tier. */
export type PlanPriceTier = {
    upTo: number | null;
    unitAmountMinor: number | null;
    flatAmountMinor: number | null;
};

/** Stripe `tiers_mode`: every unit is priced at the tier the total quantity lands in. */
export const VOLUME_MODE = 'volume';

/** Stripe `tiers_mode`: each tier prices only the units that fall inside it. */
export const GRADUATED_MODE = 'graduated';

/**
 * Total charge per billing interval for `seats` seats, in minor units.
 *
 * `unitAmountMinor` is the price's own `unit_amount` — null on a tiered price, which is precisely
 * why reading `unitAmount ?? 0` off a tiered price renders it as $0. Seats below 1 clamp to 1:
 * there is no zero-seat subscription.
 */
export function seatTotalMinor(
    unitAmountMinor: number | null | undefined,
    tiersMode: string | null | undefined,
    tiers: readonly PlanPriceTier[] | null | undefined,
    seats: number,
): number {
    const quantity = seats < 1 ? 1 : Math.floor(seats);

    if (!tiers || tiers.length === 0) {
        // Simple per-unit price (Marketplace's $0 included): quantity × unit_amount.
        return Math.max(0, (unitAmountMinor ?? 0) * quantity);
    }

    const total = tiersMode?.toLowerCase() === GRADUATED_MODE
        ? graduatedTotal(tiers, quantity)
        // Volume is both the configured mode for Intelligence and the safer default: an unknown
        // tiers_mode falls here rather than silently returning 0.
        : volumeTotal(tiers, quantity);

    return Math.max(0, total);
}

// Volume: the whole quantity is priced at the single tier it lands in.
//   Intelligence (tiers_mode=volume):
//     tier 1  up_to 1     unit 0     flat 2999
//     tier 2  up_to inf   unit 999   flat 1999
//   1 seat  -> tier 1: 2999 + 0×1   = 2999  ($29.99)
//   2 seats -> tier 2: 1999 + 999×2 = 3997  ($39.97)
//   3 seats -> tier 2: 1999 + 999×3 = 4996  ($49.96)
function volumeTotal(tiers: readonly PlanPriceTier[], quantity: number): number {
    const tier = findTier(tiers, quantity);
    return (tier.flatAmountMinor ?? 0) + (tier.unitAmountMinor ?? 0) * quantity;
}

// Graduated: each tier charges only for the units that fall within its own band. Not how
// Intelligence is configured today, but tiers_mode comes off the live price — if someone switches
// the price in the Dashboard, quoting it as volume would overcharge or undercharge.
function graduatedTotal(tiers: readonly PlanPriceTier[], quantity: number): number {
    let total = 0;
    let consumed = 0;

    for (const tier of tiers) {
        if (consumed >= quantity) break;

        const bandTop = tier.upTo ?? quantity;
        const unitsInBand = Math.min(quantity, bandTop) - consumed;
        if (unitsInBand <= 0) continue;

        total += (tier.flatAmountMinor ?? 0) + (tier.unitAmountMinor ?? 0) * unitsInBand;
        consumed += unitsInBand;
    }

    // Quantity above the last bounded tier with no unbounded tier configured: charge the remainder
    // at the last tier's unit rate rather than giving the overflow away free.
    if (consumed < quantity && tiers.length > 0) {
        const last = tiers[tiers.length - 1];
        total += (last.unitAmountMinor ?? 0) * (quantity - consumed);
    }

    return total;
}

// First tier whose up_to covers the quantity; the unbounded (up_to = null) tier covers anything
// left. Falls back to the last tier when every tier is bounded below the quantity.
function findTier(tiers: readonly PlanPriceTier[], quantity: number): PlanPriceTier {
    for (const tier of tiers) {
        if (tier.upTo === null || tier.upTo === undefined || quantity <= tier.upTo) return tier;
    }
    return tiers[tiers.length - 1];
}

/** "$29.99" — minor units to a displayable AUD amount. Whole dollars drop the cents ("$0"). */
export function formatAudMinor(minor: number): string {
    const dollars = minor / 100;
    return `$${Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}
